import { useEffect, useMemo } from 'react'

import { DataSchemaItemFragment } from 'generated/resources/graphql'
import { camelCase } from 'lodash'

import Selector from 'modules/core/components/Selector'
import i18n from 'modules/core/lib/i18n'

import { useCountryDataFieldChange } from 'modules/employees/components/UnifiedAddEmployeeModal/CountryDataInputsContext'

interface Props {
  dataSchemaItem?: DataSchemaItemFragment
  value: string | unknown
  onChange: (fieldName: string, value: string) => void
  mandatory?: boolean
  error?: string
  legalEntityId?: string
  country?: string
}

export const EnumField = ({
  dataSchemaItem,
  value,
  onChange,
  mandatory,
  error,
  legalEntityId,
  country,
}: Props) => {
  const updateCountryDataField = useCountryDataFieldChange()

  const countryDataFieldName = dataSchemaItem?.metadata?.find(
    (m) => m.key === 'country_data_field_name'
  )?.value

  const allowedValues = useMemo(() => {
    let values = dataSchemaItem?.allowedValues ?? []
    if (legalEntityId) {
      values = values.filter(
        (option) => !option.legalEntityIds || option.legalEntityIds?.includes(legalEntityId)
      )
    }
    if (!!country && dataSchemaItem?.fieldName !== 'legal_entity_id') {
      values = values.filter((option) => !option.country || option.country === country)
    }
    return values
  }, [dataSchemaItem, legalEntityId, country])

  // Sync country data input: clear when field becomes unavailable,
  // re-sync current value when field becomes available again
  useEffect(() => {
    if (!countryDataFieldName || !updateCountryDataField) return

    if (!allowedValues.length) {
      updateCountryDataField(countryDataFieldName, null)
    } else if (value) {
      updateCountryDataField(countryDataFieldName, String(value))
    }
  }, [allowedValues.length, countryDataFieldName, updateCountryDataField, value])

  if (!dataSchemaItem) {
    return null
  }

  if (!allowedValues.length) return null

  return (
    <Selector.Popover
      blankLabel={i18n.t('employee_form.payroll_data.select_an_option')}
      placeholder={i18n.t('employee_form.payroll_data.select_an_option')}
      searchPlaceholder={dataSchemaItem.label}
      inputStyle="badge"
      label={dataSchemaItem.label}
      onChange={(newValue) => {
        const stringValue = newValue ? String(newValue) : ''
        onChange(camelCase(dataSchemaItem.fieldName), stringValue)
        if (countryDataFieldName && updateCountryDataField) {
          updateCountryDataField(countryDataFieldName, stringValue || null)
        }
      }}
      value={value ? [value] : []}
      isMultiSelect={false}
      mandatory={mandatory}
      error={error}
    >
      {allowedValues.map((allowedValue) => (
        <Selector.Option
          key={allowedValue.key}
          value={allowedValue.key}
          label={allowedValue.value}
          searchValue={allowedValue.value}
        >
          {allowedValue.value}
        </Selector.Option>
      ))}
    </Selector.Popover>
  )
}
